import { useCallback, useEffect, useRef, useState } from 'react';
import { createBatchRun, getBatchRun } from './structuredDataApi';

const POLL_MS = 1500;

export const batchIsSettled = (batch) => !!batch && batch.status !== 'queued' && batch.status !== 'running';

// Fire a batch run and poll it to completion. Shared by the contracts-list
// bulk bar and the admin editor's run-against-matching pane. The batch keeps
// running server-side if the caller unmounts — polling just stops.
export default function useBatchRun({ onSettled } = {}) {
  const [batch, setBatch] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const timer = useRef(null);
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  const stopPolling = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = null;
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const poll = useCallback((schemaId, batchId) => {
    timer.current = setTimeout(async () => {
      try {
        const next = await getBatchRun(schemaId, batchId);
        setBatch(next);
        if (batchIsSettled(next)) {
          onSettledRef.current?.(next);
        } else {
          poll(schemaId, batchId);
        }
      } catch (err) {
        // Transient poll failure (network blip): keep polling — the batch is
        // durable server-side and the next tick usually recovers.
        poll(schemaId, batchId);
      }
    }, POLL_MS);
  }, []);

  const start = useCallback(async (schemaId, selection) => {
    setStarting(true);
    setError(null);
    setBatch(null);
    try {
      const created = await createBatchRun(schemaId, selection);
      setBatch(created);
      if (batchIsSettled(created)) {
        onSettledRef.current?.(created);
      } else {
        poll(schemaId, created.id);
      }
      return created;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setStarting(false);
    }
  }, [poll]);

  const reset = useCallback(() => {
    stopPolling();
    setBatch(null);
    setError(null);
  }, [stopPolling]);

  return {
    batch,
    starting,
    error,
    start,
    reset,
    inFlight: !!batch && !batchIsSettled(batch),
    settled: batchIsSettled(batch),
  };
}
